import { useCallback, useEffect, useRef, useState } from 'react';

import { Box, useComputedColorScheme } from '@mantine/core';

import { Scene1Noise } from './Scene1Noise';
import { Scene2Turn } from './Scene2Turn';
import { Scene3Grade } from './Scene3Grade';
import { Scene4Strata } from './Scene4Strata';
import { Scene5Voice } from './Scene5Voice';
import { Scene6Wordmark } from './Scene6Wordmark';
import { DURATION, STAGE_HEIGHT, STAGE_WIDTH } from './storyboard';

interface AboutIntroOverlayProps {
  onEnd: () => void;
}

/**
 * Organism - the six-scene intro film on /about.
 *
 * This component is only the projector: it drives a single clock, scales the
 * fixed 1600x900 canvas to fit its container, and renders the chrome. Every
 * scene is mounted on each frame and decides for itself whether it is on
 * screen, so adding or reordering a scene means touching ./storyboard.ts and
 * one Scene file, never this one.
 */
export function AboutIntroOverlay({ onEnd }: AboutIntroOverlayProps) {
  const colorScheme = useComputedColorScheme('dark');
  const logoSrc = colorScheme === 'light' ? '/images/light/horiz.png' : '/images/dark/horiz.png';

  const [time, setTime] = useState(0);
  const [scale, setScale] = useState(1);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);
  const endedRef = useRef(false);

  const end = useCallback(() => {
    if (endedRef.current) return;
    endedRef.current = true;
    onEnd();
  }, [onEnd]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const compute = () => {
      const rect = el.getBoundingClientRect();
      const s = Math.min(rect.width / STAGE_WIDTH, rect.height / STAGE_HEIGHT);
      setScale(s > 0 ? s : 1);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const step = (ts: number) => {
      startRef.current ??= ts;
      const t = (ts - startRef.current) / 1000;
      if (t >= DURATION) {
        setTime(DURATION);
        end();
        return;
      }
      setTime(t);
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [end]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code === 'Escape' || e.code === 'Enter' || e.code === 'Space') {
        e.preventDefault();
        end();
      }
    };
    globalThis.addEventListener('keydown', onKey);
    return () => globalThis.removeEventListener('keydown', onKey);
  }, [end]);

  return (
    <Box
      ref={containerRef}
      aria-label="About intro animation"
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: 1200,
        margin: '0 auto',
        aspectRatio: `${STAGE_WIDTH} / ${STAGE_HEIGHT}`,
        overflow: 'hidden',
      }}
    >
      {/* Scaled canvas */}
      <Box
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          style={{
            width: STAGE_WIDTH,
            height: STAGE_HEIGHT,
            position: 'relative',
            transform: `scale(${scale})`,
            transformOrigin: 'center',
            background: 'var(--assay-bg)',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {/* warm radial wash + grain inside the canvas */}
          <Box
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              zIndex: 0,
              background:
                'radial-gradient(ellipse 60% 50% at 0% 0%, rgba(196,152,58,0.07), transparent 60%),' +
                'radial-gradient(ellipse 50% 40% at 100% 100%, rgba(58,140,128,0.05), transparent 60%),' +
                'radial-gradient(ellipse 50% 35% at 50% 100%, rgba(196,152,58,0.05), transparent 70%)',
            }}
          />
          <Scene1Noise time={time} />
          <Scene2Turn time={time} />
          <Scene3Grade time={time} />
          <Scene4Strata time={time} />
          <Scene5Voice time={time} />
          <Scene6Wordmark time={time} logoSrc={logoSrc} />
        </Box>
      </Box>

      {/* Chrome: skip button (top-right) */}
      <Box style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 60 }}>
        <button
          type="button"
          onClick={end}
          title="Skip (Esc)"
          style={{
            position: 'absolute',
            top: 18,
            right: 22,
            pointerEvents: 'auto',
            background: 'transparent',
            border: '1px solid var(--assay-ore-border-hover)',
            color: 'var(--assay-text-body)',
            padding: '8px 12px',
            fontFamily: '"DM Mono", monospace',
            fontSize: '0.55rem',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            borderRadius: 2,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            backdropFilter: 'blur(2px)',
          }}
        >
          Skip intro
        </button>
      </Box>
    </Box>
  );
}
